import { CameraAlt, VideoCameraFront } from '@mui/icons-material';
import SentimentSatisfiedOutlinedIcon from '@mui/icons-material/SentimentSatisfiedOutlined';
import { CircularProgress } from '@mui/material';
import Picker from 'emoji-picker-react';
import { FC, useEffect, useRef, useState } from 'react';
import { commentsApi } from 'src/api/comments';
import { AutoGrowTextarea } from 'src/components/common/Forms/AutoGrowTextarea';
import { ImageModal } from './ImageModal/ImageModal';
import styles from './PostCommentInput.module.scss';
import { VideoModal } from './VideoModal/VideoModal';

interface PostCommentInputProps {
  postId: string;
  commentId?: string;
  isReply?: boolean;
  onCommentPosted?: (newComment: any) => void;
}

export const PostCommentInput: FC<PostCommentInputProps> = ({
  postId,
  commentId,
  isReply,
  onCommentPosted,
}) => {
  const [commentText, setCommentText] = useState('');
  const [isEmojiOpen, setIsEmojiOpen] = useState<boolean>(false);
  const [mediaType, setMediaType] = useState<'image' | 'video' | null>(null);
  const [mediaId, setMediaId] = useState<string | null>(null);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [isPosting, setIsPosting] = useState(false);

  const emojiPickerRef = useRef<HTMLDivElement>(null);

  const toggleEmojiPicker = () => setIsEmojiOpen((prev) => !prev);

  const onEmojiClick = (emojiObject: any) => {
    setCommentText((prevText) => prevText + emojiObject.emoji);
  };

  const postComment = async () => {
    if (commentText.trim() === '' && !mediaId) {
      return;
    }

    setIsPosting(true);

    const data: any = {
      ...(commentText && { text: commentText }),
      ...(isReply && commentId ? { parent_id: commentId } : {}),
    };

    if (mediaType && mediaId) {
      data.media_type = mediaType;
      data.media = { file_id: mediaId };
    }

    try {
      const newComment = await commentsApi.postComment(postId, data);
      setCommentText('');
      setMediaId(null);
      setMediaType(null);
      if (newComment && onCommentPosted) {
        onCommentPosted(newComment);
      }
    } catch (error) {
      console.error('Error posting the comment:', error);
    } finally {
      setIsPosting(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        emojiPickerRef.current &&
        !emojiPickerRef.current.contains(event.target as Node)
      ) {
        setIsEmojiOpen(false);
      }
    };
    if (isEmojiOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isEmojiOpen]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      setCommentText((prev) => prev + '\n');
      e.preventDefault();
    } else if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey && !e.metaKey) {
      postComment();
      e.preventDefault();
    }
  };

  return (
    <>
      <div className={styles.commentInputContainer}>
        <div className={styles.inputWrapper}>
          <AutoGrowTextarea
            placeholder={'Type your message...'}
            className={styles.commentInput}
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <SentimentSatisfiedOutlinedIcon
            onClick={toggleEmojiPicker}
            className={styles.emojiIcon}
          />
        </div>

        {isEmojiOpen && (
          <div ref={emojiPickerRef} className={styles.emojiPickerContainer}>
            <Picker onEmojiClick={onEmojiClick} />
          </div>
        )}
      </div>
      <div className={styles.postInputBottom}>
        <span>
          {!isReply && (
            <div className={styles.icons}>
              <CameraAlt
                sx={{ cursor: 'pointer' }}
                className={styles.mediaIcon}
                onClick={() => setImageModalOpen(true)}
              />
              <VideoCameraFront
                sx={{ cursor: 'pointer' }}
                className={styles.mediaIcon}
                onClick={() => setVideoModalOpen(true)}
              />
            </div>
          )}
        </span>

        <button
          className={isReply ? styles.postOutlinedButton : styles.postButton}
          disabled={isPosting}
          onClick={postComment}
        >
          {isPosting ? (
            <CircularProgress size={24} sx={{ color: 'white' }} />
          ) : (
            <span>{isReply ? 'Reply' : 'Send'}</span>
          )}
        </button>
      </div>
      {!isReply && (
        <>
          <ImageModal
            isOpenModal={imageModalOpen}
            setIsOpenModal={setImageModalOpen}
            postId={postId}
            onMediaUploaded={(fileId) => {
              setMediaType('image');
              setMediaId(fileId);
            }}
            onCommentPosted={onCommentPosted}
            isReply={isReply}
            parentCommentId={commentId}
          />
          <VideoModal
            isOpenModal={videoModalOpen}
            setIsOpenModal={setVideoModalOpen}
            postId={postId}
            onMediaUploaded={(fileId) => {
              setMediaType('video');
              setMediaId(fileId);
            }}
            onCommentPosted={onCommentPosted}
            isReply={isReply}
            parentCommentId={commentId}
          />
        </>
      )}
    </>
  );
};
